import { useEffect, useState } from 'react';
import { 
  Search, 
  Filter, 
  RefreshCw, 
  LogOut, 
  User, 
  Video, 
  FileText, 
  Sparkles,
  TrendingUp,
  Clock,
  Users,
  ChevronRight
} from 'lucide-react';
import { MeetingCard } from '../components/MeetingCard';
import { MeetingDetail } from '../components/MeetingDetail';
import { useMeetings } from '../hooks/useMeetings';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import type { MeetingFilters } from '../types';

/**
 * Dashboard Page
 * Ana sayfa - toplantı listesi ve detay görünümü
 */
export function Dashboard() {
  const { user, logout, isAuthenticated, isLoading: authLoading, getAccessToken } = useAuth();
  const {
    meetings,
    selectedMeeting,
    isLoading,
    error,
    fetchMeetings,
    fetchMeetingDetail,
    generateAnalysis,
    reanalyze,
    exportMeeting,
    checkNewMeetings,
    clearSelectedMeeting
  } = useMeetings();

  // Varsayılan: Son 7 gün, tüm toplantılar (transcript olan ve olmayan)
  const getDefaultFilters = (): MeetingFilters => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);

    return {
      withTranscriptsOnly: false,
      searchQuery: '',
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  };

  const [filters, setFilters] = useState<MeetingFilters>(getDefaultFilters());
  const [showFilters, setShowFilters] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Token hazır olduğunda toplantıları getir
  useEffect(() => {
    const initializeDashboard = async () => {
      if (isAuthenticated && !authLoading && !isInitialized) {
        // Token'ı al ve API servisine set et
        const token = await getAccessToken();
        if (token) {
          // ÖNEMLİ: Token'ı API servisine set et
          apiService.setAccessToken(token);
          setIsInitialized(true);
          fetchMeetings(filters);
        }
      }
    };

    initializeDashboard();
  }, [isAuthenticated, authLoading, isInitialized, getAccessToken]);

  // Filtreleri uygula
  const applyFilters = () => {
    fetchMeetings(filters);
    setShowFilters(false);
  };

  // Filtreleri temizle - Tüm toplantıları göster (6 ay)
  const clearFilters = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 6);

    const emptyFilters: MeetingFilters = {
      withTranscriptsOnly: false,
      searchQuery: '',
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
    setFilters(emptyFilters);
    fetchMeetings(emptyFilters);
  };

  // Hızlı filtre uygula
  const applyQuickFilter = (days: number) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const quickFilters: MeetingFilters = {
      ...filters,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
    setFilters(quickFilters);
    fetchMeetings(quickFilters);
  };

  // Yeni toplantıları kontrol et (Graph API'den)
  const handleCheckNewMeetings = async () => {
    const token = await getAccessToken();
    if (token) {
      apiService.setAccessToken(token);
      checkNewMeetings(filters);
    }
  };

  // Arama
  const handleSearch = (query: string) => {
    setFilters(prev => ({ ...prev, searchQuery: query }));
    // Debounce ile arama
    const timeoutId = setTimeout(() => {
      fetchMeetings({ ...filters, searchQuery: query });
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  // Toplantı kartına tıklandığında
  const handleMeetingClick = (meetingId: string) => {
    fetchMeetingDetail(meetingId);
  };

  // Yenile
  const handleRefresh = async () => {
    const token = await getAccessToken();
    if (token) {
      apiService.setAccessToken(token);
      fetchMeetings(filters);
    }
  };

  // Dışa aktarma
  const handleExport = async (format: 'pdf' | 'docx', includeTranscript: boolean) => {
    if (selectedMeeting) {
      await exportMeeting(selectedMeeting.meeting.id, format, includeTranscript);
    }
  };

  // Özet Çıkar (ilk kez analiz oluştur)
  const handleGenerateAnalysis = () => {
    if (selectedMeeting) {
      generateAnalysis(selectedMeeting.meeting.id);
    }
  };

  // Yeniden analiz et
  const handleReanalyze = () => {
    if (selectedMeeting) {
      reanalyze(selectedMeeting.meeting.id);
    }
  };

  // Email gönder
  const handleSendEmail = async (
    contentType: 'full' | 'summary',
    recipientType: string,
    customRecipients?: string[]
  ) => {
    if (!selectedMeeting) return;

    try {
      const response = await apiService.sendTranscriptEmail(
        selectedMeeting.meeting.id,
        contentType,
        recipientType as 'internal' | 'external' | 'all' | 'custom',
        customRecipients
      );

      if (!response.success) {
        const errorMessage = typeof response.error === 'string'
          ? response.error
          : response.error?.message || 'Email gönderilemedi';
        throw new Error(errorMessage);
      }

      // Success - modal will show success message
    } catch (error: any) {
      throw new Error(error.message || 'Email gönderilemedi');
    }
  };

  // Detay görünümündeyken
  if (selectedMeeting) {
    return (
      <MeetingDetail
        data={selectedMeeting}
        onBack={clearSelectedMeeting}
        onExport={handleExport}
        onGenerateAnalysis={handleGenerateAnalysis}
        onReanalyze={handleReanalyze}
        onSendEmail={handleSendEmail}
        isLoading={isLoading}
      />
    );
  }

  // Toplantı sayıları
  const totalMeetings = meetings.length;
  const meetingsWithTranscript = meetings.filter(m => m.hasTranscript).length;
  const meetingsWithAnalysis = meetings.filter(m => m.hasAnalysis).length;

  // Auth yükleniyorsa
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto" />
            <Sparkles className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-indigo-600" />
          </div>
          <p className="mt-4 text-slate-600 font-medium">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
                  <Video size={24} className="text-white" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                  Teams Transcript Manager
                </h1>
                <p className="text-sm text-slate-500">Toplantı transkriptlerinizi AI ile analiz edin</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full">
                <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                  <User size={16} className="text-white" />
                </div>
                <span className="text-sm font-medium text-slate-700">{user?.displayName}</span>
              </div>
              <button
                onClick={logout}
                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-full transition-all duration-200"
              >
                <LogOut size={18} />
                <span className="hidden sm:inline">Çıkış</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Toplam Toplantı */}
          <div className="group relative bg-white rounded-2xl shadow-sm border border-slate-200/50 p-6 hover:shadow-lg hover:shadow-indigo-500/10 transition-all duration-300 overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Video size={28} className="text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">{totalMeetings}</p>
                <p className="text-sm text-slate-500 flex items-center gap-1">
                  <TrendingUp size={14} />
                  Toplam Toplantı
                </p>
              </div>
            </div>
          </div>

          {/* Transkriptli */}
          <div className="group relative bg-white rounded-2xl shadow-sm border border-slate-200/50 p-6 hover:shadow-lg hover:shadow-emerald-500/10 transition-all duration-300 overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-500/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/25">
                <FileText size={28} className="text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">{meetingsWithTranscript}</p>
                <p className="text-sm text-slate-500 flex items-center gap-1">
                  <Clock size={14} />
                  Transkriptli
                </p>
              </div>
            </div>
          </div>

          {/* Analiz Edilmiş */}
          <div className="group relative bg-white rounded-2xl shadow-sm border border-slate-200/50 p-6 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300 overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25">
                <Sparkles size={28} className="text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">{meetingsWithAnalysis}</p>
                <p className="text-sm text-slate-500 flex items-center gap-1">
                  <Users size={14} />
                  AI Analiz Edilmiş
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 p-4 mb-8">
          {/* Hızlı Filtreler */}
          <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-slate-100">
            <button
              onClick={() => applyQuickFilter(7)}
              className="px-4 py-2 bg-slate-50 text-slate-700 rounded-lg text-sm font-medium hover:bg-indigo-50 hover:text-indigo-700 transition-all duration-200"
            >
              Son 7 Gün
            </button>
            <button
              onClick={() => applyQuickFilter(30)}
              className="px-4 py-2 bg-slate-50 text-slate-700 rounded-lg text-sm font-medium hover:bg-indigo-50 hover:text-indigo-700 transition-all duration-200"
            >
              Son 1 Ay
            </button>
            <button
              onClick={() => applyQuickFilter(90)}
              className="px-4 py-2 bg-slate-50 text-slate-700 rounded-lg text-sm font-medium hover:bg-indigo-50 hover:text-indigo-700 transition-all duration-200"
            >
              Son 3 Ay
            </button>
            <button
              onClick={() => applyQuickFilter(180)}
              className="px-4 py-2 bg-slate-50 text-slate-700 rounded-lg text-sm font-medium hover:bg-indigo-50 hover:text-indigo-700 transition-all duration-200"
            >
              Son 6 Ay
            </button>
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-slate-50 text-slate-700 rounded-lg text-sm font-medium hover:bg-purple-50 hover:text-purple-700 transition-all duration-200"
            >
              Tüm Toplantılarım
            </button>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Toplantı veya organizatör ara..."
                value={filters.searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white transition-all duration-200"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-5 py-3 border rounded-xl font-medium transition-all duration-200 ${
                showFilters
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-500/25'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
              }`}
            >
              <Filter size={18} />
              <span>Gelişmiş</span>
            </button>

            {/* Check New Meetings */}
            <button
              onClick={handleCheckNewMeetings}
              disabled={isLoading}
              className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-medium hover:from-emerald-700 hover:to-teal-700 shadow-lg shadow-emerald-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Graph API'den yeni toplantıları kontrol et"
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">
                {isLoading ? 'Kontrol ediliyor...' : 'Yeni Toplantılar'}
              </span>
            </button>

            {/* Refresh */}
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:from-indigo-700 hover:to-purple-700 shadow-lg shadow-indigo-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
              <span>{isLoading ? 'Yükleniyor...' : 'Yenile'}</span>
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Başlangıç Tarihi</label>
                  <input
                    type="date"
                    value={filters.startDate || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Bitiş Tarihi</label>
                  <input
                    type="date"
                    value={filters.endDate || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 focus:bg-white transition-all duration-200"
                  />
                </div>
                <div className="flex flex-col gap-3 justify-end">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={filters.withTranscriptsOnly}
                      onChange={(e) => setFilters(prev => ({ ...prev, withTranscriptsOnly: e.target.checked }))}
                      className="w-5 h-5 text-indigo-600 rounded-md border-slate-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-slate-700 group-hover:text-slate-900 transition-colors">
                      Sadece transkriptli toplantılar
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={filters.myMeetingsOnly}
                      onChange={(e) => setFilters(prev => ({ ...prev, myMeetingsOnly: e.target.checked }))}
                      className="w-5 h-5 text-indigo-600 rounded-md border-slate-300 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-slate-700 group-hover:text-slate-900 transition-colors">
                      Sadece benim organize ettiğim
                    </span>
                  </label>
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={clearFilters}
                  className="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-all duration-200"
                >
                  Filtreleri Temizle
                </button>
                <button
                  onClick={applyFilters}
                  className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:from-indigo-700 hover:to-purple-700 shadow-lg shadow-indigo-500/25 transition-all duration-200"
                >
                  Filtreleri Uygula
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-xl mb-8 flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-red-500 text-xl">!</span>
            </div>
            <div>
              <p className="font-medium">Bir hata oluştu</p>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Meeting List */}
        {isLoading && meetings.length === 0 ? (
          <div className="text-center py-16">
            <div className="relative inline-block">
              <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
              <Sparkles className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-indigo-600" />
            </div>
            <p className="mt-4 text-slate-600 font-medium">Toplantılar yükleniyor...</p>
            <p className="text-sm text-slate-400 mt-1">Lütfen bekleyin</p>
          </div>
        ) : meetings.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-slate-200/50">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Video size={40} className="text-slate-400" />
            </div>
            <p className="text-slate-600 font-medium text-lg">Henüz toplantı bulunmuyor</p>
            <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">
              Teams toplantılarınız burada görünecek. Transkript özelliği açık toplantılar otomatik olarak listelenecek.
            </p>
            <button
              onClick={handleRefresh}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors"
            >
              <RefreshCw size={18} />
              Yenile
            </button>
          </div>
        ) : (
          <>
            {/* Info Banner */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-4 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles size={20} className="text-indigo-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-indigo-900">
                  <strong>İpucu:</strong> Toplantı kartına tıklayarak transkripti görüntüleyin ve AI analiz oluşturun.
                </p>
              </div>
              <ChevronRight size={20} className="text-indigo-400" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {meetings.map((meeting) => (
                <MeetingCard
                  key={meeting.id}
                  meeting={meeting}
                  onClick={() => handleMeetingClick(meeting.id)}
                />
              ))}
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-auto py-6 text-center text-sm text-slate-400">
        <p>© 2026 Teams Transcript Manager • AI Destekli Toplantı Analizi</p>
      </footer>
    </div>
  );
}
